import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DollarSign, Edit, UserPlus, UserX } from 'react-feather';
import DataTable from '../../../Common/DataTable';
import { Btn } from '../../../../AbstractElements';
import PaymentCollectionDataModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionDataModal';
import PaymentCollectionListModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionListModal';
import { fetchFormData, fetchTableData, fetchUsersByRole, assignTaskToUser, resetTaskAssignment, createFinalCompensation } from '../../../../Api/ApiMethods';
import { shouldToastError } from '../../../../Api/utils';
import { normalizeFieldValue } from '../../../../_helper/regexHandler';

const TodoTabEnforcement = ({ onCountChange, onLoadingChange }) => {
    const navigate = useNavigate();
    const isInitialLoad = useRef(true);
    const searchRef = useRef({ caseNumberSearch: '', tcIdSearch: '', accidentDateSearch: '' });

    const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [caseNumberSearch, setCaseNumberSearch] = useState('');
    const [tcIdSearch, setTcIdSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');

    const [arbitrationStates, setArbitrationStates] = useState([]);
    const [selectedState, setSelectedState] = useState('');
    const [selectedSentenced, setSelectedSentenced] = useState('');

    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [enforcementToAssignId, setEnforcementToAssignId] = useState(null);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [isRemovingAssignment, setIsRemovingAssignment] = useState(false);

    // Tahsilat state'leri
    const [isPaymentCollectionListModalOpen, setIsPaymentCollectionListModalOpen] = useState(false);
    const [paymentCollections, setPaymentCollections] = useState([]);
    const [isPaymentCollectionsLoading, setIsPaymentCollectionsLoading] = useState(false);
    const [currentAccidentFileForPayments, setCurrentAccidentFileForPayments] = useState(null);
    const [paymentCollectionModalOpen, setPaymentCollectionModalOpen] = useState(false);
    const [paymentCollectionModalMode, setPaymentCollectionModalMode] = useState('add');
    const [selectedPaymentCollection, setSelectedPaymentCollection] = useState(null);
    const [finalCompensationSubmittingId, setFinalCompensationSubmittingId] = useState(null);

    useEffect(() => {
        if (typeof onCountChange === 'function') {
            onCountChange(totalRows);
        }
    }, [onCountChange, totalRows]);

    useEffect(() => {
        if (typeof onLoadingChange === 'function') {
            onLoadingChange(loading);
        }
    }, [loading, onLoadingChange]);

    useEffect(() => {
        searchRef.current = {
            caseNumberSearch,
            tcIdSearch,
            accidentDateSearch
        };
    }, [caseNumberSearch, tcIdSearch, accidentDateSearch]);

    useEffect(() => {
        const handleRoleChange = () => {
            setUserRole(localStorage.getItem('userRole'));
        };

        window.addEventListener('userRoleChanged', handleRoleChange);

        return () => {
            window.removeEventListener('userRoleChanged', handleRoleChange);
        };
    }, []);

    useEffect(() => {
        const fetchEnforcementArbitrationStates = async () => {
            try {
                const queryParams = new URLSearchParams();
                queryParams.append('isEnforcement', 'true');
                queryParams.append('page', '1');
                queryParams.append('pageSize', '100');

                const response = await fetchFormData({
                    apiEndpoint: process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/ArbitrationState?' + queryParams.toString(),
                    page: 1,
                    pageSize: 100
                });

                const allItems = Array.isArray(response) ? response : [];
                setArbitrationStates(allItems);
            } catch (error) {
                toast.error('Tahkim durumları alınırken bir hata oluştu.');
                setArbitrationStates([]);
            }
        };

        fetchEnforcementArbitrationStates();
    }, []);

    const paymentCollectionApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/paymentcollection';

    const fetchEnforcementData = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Arbitration';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('isEnforcement', 'true');

            const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;
            if (hasCaseNumberSearch) {
                queryParams.append('caseNumber', caseNumberSearch.trim());
            }

            const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length >= 3;
            const hasAccidentDateSearch = accidentDateSearch && accidentDateSearch.trim().length > 0;

            if (!hasCaseNumberSearch) {
                if (hasTcIdSearch) {
                    queryParams.append('tcId', tcIdSearch.trim());
                }
                if (hasAccidentDateSearch) {
                    queryParams.append('accidentDate', accidentDateSearch);
                }
            }

            if (!hasCaseNumberSearch && !hasTcIdSearch && !hasAccidentDateSearch) {
                if (selectedState && selectedState !== '') {
                    queryParams.append('stateId', selectedState);
                }
                if (selectedSentenced !== '') {
                    queryParams.append('isSentenced', selectedSentenced);
                }
            }

            const fullEndpoint = queryParams.toString()
                ? `${apiEndpoint}?${queryParams.toString()}`
                : `${apiEndpoint}?isEnforcement=true`;

            await fetchTableData({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setItems,
                setTotalRowsCallback: setTotalRows,
            });
        } catch (error) {
            toast.error('İcra verileri alınırken bir hata oluştu.');
            setItems([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, caseNumberSearch, tcIdSearch, accidentDateSearch, selectedState, selectedSentenced]);

    const fetchEnforcementDataNonSearch = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Arbitration';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('isEnforcement', 'true');

            const { caseNumberSearch: currentCaseSearch, tcIdSearch: currentTcIdSearch, accidentDateSearch: currentAccidentDateSearch } = searchRef.current;

            const hasCaseNumberSearch = currentCaseSearch && currentCaseSearch.trim().length >= 3;
            if (hasCaseNumberSearch) {
                queryParams.append('caseNumber', currentCaseSearch.trim());
            }

            const hasTcIdSearch = currentTcIdSearch && currentTcIdSearch.trim().length >= 3;
            const hasAccidentDateSearch = currentAccidentDateSearch && currentAccidentDateSearch.trim().length > 0;

            if (!hasCaseNumberSearch) {
                if (hasTcIdSearch) {
                    queryParams.append('tcId', currentTcIdSearch.trim());
                }
                if (hasAccidentDateSearch) {
                    queryParams.append('accidentDate', currentAccidentDateSearch);
                }
            }

            if (!hasCaseNumberSearch && !hasTcIdSearch && !hasAccidentDateSearch) {
                if (selectedState && selectedState !== '') {
                    queryParams.append('stateId', selectedState);
                }
                if (selectedSentenced !== '') {
                    queryParams.append('isSentenced', selectedSentenced);
                }
            }

            const fullEndpoint = queryParams.toString()
                ? `${apiEndpoint}?${queryParams.toString()}`
                : `${apiEndpoint}?isEnforcement=true`;

            await fetchTableData({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setItems,
                setTotalRowsCallback: setTotalRows,
            });
        } catch (error) {
            toast.error('İcra verileri alınırken bir hata oluştu.');
            setItems([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, selectedState, selectedSentenced]);

    // Pagination ve non-search filtre (state/sentenced) değişiminde anında fetch
    useEffect(() => {
        fetchEnforcementDataNonSearch();
        // fetchEnforcementDataNonSearch zaten [page, pageSize, selectedState, selectedSentenced] deps'i ile yeniden oluşuyor; burada tekrar dep olarak eklemek çift fetch'e yol açar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, selectedState, selectedSentenced]);

    // Search input'ları için debounce'lu fetch; callback'ler dep olarak dahil edilmez — aksi halde non-search filtreler değiştiğinde de fire edip çift fetch olur (jitter).
    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        const hasAnySearch = caseNumberSearch.trim() || tcIdSearch.trim() || accidentDateSearch.trim();
        const timeoutId = setTimeout(() => {
            const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length > 0;
            const hasValidCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;
            const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length > 0;
            const hasValidTcIdSearch = tcIdSearch && tcIdSearch.trim().length >= 3;

            if (hasAnySearch) {
                if ((hasCaseNumberSearch && !hasValidCaseNumberSearch) || (hasTcIdSearch && !hasValidTcIdSearch)) {
                    return;
                }
                fetchEnforcementData();
            } else {
                fetchEnforcementDataNonSearch();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseNumberSearch, tcIdSearch, accidentDateSearch]);

    const handleCaseNumberSearchChange = (event) => {
        const inputValue = event.target.value;
        const normalizedValue = normalizeFieldValue('caseNumberSearch', inputValue);

        setCaseNumberSearch(normalizedValue);
        setPage(1);

        if (normalizedValue && normalizedValue.trim().length >= 3) {
            setTcIdSearch('');
            setAccidentDateSearch('');
            setSelectedState('');
            setSelectedSentenced('');
        }
    };

    const handleTcIdSearchChange = (event) => {
        const inputValue = event.target.value;
        const numericValue = normalizeFieldValue('tcIdSearch', inputValue);

        setTcIdSearch(numericValue);
        setPage(1);

        if (numericValue && numericValue.trim().length > 0) {
            setCaseNumberSearch('');
            setSelectedState('');
            setSelectedSentenced('');
        }
    };

    const handleAccidentDateSearchChange = (event) => {
        const dateValue = event.target.value;

        setAccidentDateSearch(dateValue);
        setPage(1);

        if (dateValue && dateValue.trim().length > 0) {
            setCaseNumberSearch('');
            setSelectedState('');
            setSelectedSentenced('');
        }
    };

    const handleArbitrationStateFilterChange = (event) => {
        setSelectedState(event.target.value);
        setPage(1);
    };

    const handleSentencedFilterChange = (event) => {
        setSelectedSentenced(event.target.value);
        setPage(1);
    };

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (newPageSize, newPage) => {
        setPageSize(newPageSize);
        setPage(newPage || 1);
    };

    const fetchEnforcementEmployees = useCallback(async (userRoleFilter = 'enforcement') => {
        try {
            const users = await fetchUsersByRole({ userRole: userRoleFilter });

            const formattedUsers = users
                .filter(user => user.id && user.firstName && user.lastName)
                .map(user => ({
                    id: user.id,
                    name: `${user.firstName} ${user.lastName}`
                }));

            setEmployeeList(formattedUsers);
        } catch (error) {
            setEmployeeList([]);
        }
    }, []);

    const toggleAssignModal = (arbitrationId = null) => {
        if (arbitrationId) {
            setSelectedUserId('');
            setEnforcementToAssignId(arbitrationId);

            const arbitrationRecord = items.find(item => item.id === arbitrationId);
            if (arbitrationRecord) {
                const stateTitle = arbitrationRecord.arbitrationState?.title || '';

                let userRoleFilter = 'enforcement';
                if (stateTitle === 'İcra Başvuru Bekleniyor') {
                    userRoleFilter = 'enforcement';
                } else if (stateTitle === 'İcra Takip') {
                    userRoleFilter = 'enforcement';
                }

                fetchEnforcementEmployees(userRoleFilter);
            }
        }
        setAssignModalOpen(!assignModalOpen);
    };

    const handleAssignUser = async () => {
        if (!enforcementToAssignId) {
            toast.error('Atanacak tahkim ID bulunamadı.');
            return;
        }
        if (!selectedUserId) {
            toast.error('Lütfen atanacak bir personel seçin.');
            return;
        }

        setIsAssigning(true);

        try {
            const responseData = await assignTaskToUser({
                resource: 'Arbitration',
                id: enforcementToAssignId,
                userId: selectedUserId
            });

            if (responseData && responseData.isSuccess !== false) {
                toast.success('İş başarıyla personele atandı!');

                if (caseNumberSearch.trim().length >= 3 || tcIdSearch.trim().length >= 3 || accidentDateSearch.trim().length >= 3) {
                    fetchEnforcementData();
                } else {
                    fetchEnforcementDataNonSearch();
                }

                toggleAssignModal();
            } else {
                const errorMessage = responseData?.errors?.userId?.[0] || responseData?.message || 'İş atanırken bir hata oluştu.';
                toast.error(errorMessage);
            }
        } catch (error) {
            toast.error('İş atanırken bir hata oluştu.');
        } finally {
            setIsAssigning(false);
        }
    };

    const handleRemoveAssignment = async (arbitrationId) => {
        if (isRemovingAssignment) return;

        setIsRemovingAssignment(true);
        try {
            const responseData = await resetTaskAssignment({
                resource: 'Arbitration',
                id: arbitrationId
            });

            if (responseData && responseData.isSuccess !== false) {
                toast.success(responseData.message || 'İş ataması başarıyla kaldırıldı!');

                if (caseNumberSearch.trim().length >= 3 || tcIdSearch.trim().length >= 3 || accidentDateSearch.trim().length >= 3) {
                    fetchEnforcementData();
                } else {
                    fetchEnforcementDataNonSearch();
                }
            } else {
                toast.error(responseData?.message || 'İş ataması kaldırılırken bir hata oluştu.');
            }
        } catch (error) {
            toast.error('İş ataması kaldırılırken bir hata oluştu.');
        } finally {
            setIsRemovingAssignment(false);
        }
    };

    // ─── Tahsilat Handler'ları ───

    const fetchPaymentCollectionsForAccidentFile = useCallback(async (accidentFileId) => {
        if (!accidentFileId) { setPaymentCollections([]); return; }
        setIsPaymentCollectionsLoading(true);
        try {
            const response = await fetchFormData({
                apiEndpoint: `${paymentCollectionApiUrl}?AccidentFile=${encodeURIComponent(String(accidentFileId))}`,
                page: 1,
                pageSize: 100,
            });
            let collections = [];
            if (response?.isSuccess && Array.isArray(response?.data?.items)) collections = response.data.items;
            else if (Array.isArray(response?.items)) collections = response.items;
            else if (Array.isArray(response)) collections = response;
            else if (response?.data && Array.isArray(response.data)) collections = response.data;
            setPaymentCollections(collections);
        } catch (error) {
            if (shouldToastError(error)) toast.error('Tahsilatlar yüklenirken bir hata oluştu.');
            setPaymentCollections([]);
        } finally {
            setIsPaymentCollectionsLoading(false);
        }
    }, [paymentCollectionApiUrl]);

    const handleOpenPaymentCollectionListModal = (row) => {
        const accidentFile = row.accidentFile;
        const enforcementId = row.enforcements
            ? (Array.isArray(row.enforcements) ? row.enforcements[0] : null)
            : null;
        setCurrentAccidentFileForPayments({
            ...accidentFile,
            _arbitrationId: row.id,
            _enforcementId: enforcementId
        });
        fetchPaymentCollectionsForAccidentFile(accidentFile?.id);
        setIsPaymentCollectionListModalOpen(true);
    };

    const closePaymentCollectionListModal = () => {
        setIsPaymentCollectionListModalOpen(false);
        setPaymentCollectionModalOpen(false);
        setSelectedPaymentCollection(null);
        setPaymentCollections([]);
        setCurrentAccidentFileForPayments(null);
    };

    const handleAddPaymentCollection = () => {
        setSelectedPaymentCollection(null);
        setPaymentCollectionModalMode('add');
        setPaymentCollectionModalOpen(true);
    };

    const handleEditPaymentCollection = (collection) => {
        setSelectedPaymentCollection(collection);
        setPaymentCollectionModalMode('modify');
        setPaymentCollectionModalOpen(true);
    };

    const togglePaymentCollectionModal = () => {
        if (paymentCollectionModalOpen) {
            setPaymentCollectionModalOpen(false);
            setTimeout(() => setSelectedPaymentCollection(null), 300);
        } else {
            setPaymentCollectionModalOpen(true);
        }
    };

    const refreshPaymentCollections = useCallback(() => {
        if (currentAccidentFileForPayments?.id) {
            fetchPaymentCollectionsForAccidentFile(currentAccidentFileForPayments.id);
        }
    }, [fetchPaymentCollectionsForAccidentFile, currentAccidentFileForPayments]);

    const handleSendPaymentCollectionToCover = async (collection) => {
        const collectionId = collection?.id || collection?._id;
        if (!collectionId) { toast.warn('Tahsilat kaydı kimliği bulunamadı.'); return; }
        if (!currentAccidentFileForPayments?.id) { toast.warn('Kaza dosyası kimliği bulunamadı.'); return; }

        setFinalCompensationSubmittingId(collectionId);
        try {
            await createFinalCompensation({
                paymentCollections: [String(collectionId)],
                accidentFile: [String(currentAccidentFileForPayments.id)],
                arbitration: currentAccidentFileForPayments._arbitrationId ? [String(currentAccidentFileForPayments._arbitrationId)] : [],
                enforcement: currentAccidentFileForPayments._enforcementId ? [String(currentAccidentFileForPayments._enforcementId)] : []
            });
            await fetchPaymentCollectionsForAccidentFile(currentAccidentFileForPayments.id);
            fetchEnforcementData();
        } catch {
            // Hata createFinalCompensation içinde handle ediliyor
        } finally {
            setFinalCompensationSubmittingId(null);
        }
    };

    const columns = useMemo(() => ([
        {
            name: 'Müvekkil Adı / Şirket Adı',
            selector: row => {
                const customer = row.accidentFile?.customer;
                if (!customer) return '-';

                if (customer.customerType === 'Kurumsal') {
                    return customer.companyName || '-';
                }
                return customer.clientName || '-';
            },
            sortable: true,
            grow: 2,
            minWidth: '200px'
        },
        {
            name: 'TC/Vergi No',
            selector: row => {
                const customer = row.accidentFile?.customer;
                if (!customer) return '-';

                if (customer.customerType === 'Kurumsal') {
                    return customer.taxNumber || '-';
                }
                return customer.identityNumber || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Dosyası No',
            selector: row => row.accidentFile?.fileNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Esas Numarası',
            selector: row => row.arbitrationInformation?.caseNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Plaka',
            selector: row => row.accidentFile?.accident?.licensePlate?.licensePlateNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => row.accidentFile?.accident?.accidentDate
                ? new Date(row.accidentFile.accident.accidentDate).toLocaleDateString('tr-TR')
                : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Talep Edilen İş',
            selector: row => {
                const filesToBeWorked = row.accidentFile?.filesToBeWorked || [];
                if (filesToBeWorked.length === 0) return '-';

                const fileTypes = filesToBeWorked
                    .map(file => {
                        const fileTypeArray = file.fileType || [];
                        return fileTypeArray.map(ft => ft.title || '').filter(title => title !== '');
                    })
                    .flat()
                    .join(', ');

                return fileTypes || '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Çalışılan Tutar',
            selector: row => {
                const amountWorked = row.accidentFile?.amountWorked;
                if (amountWorked) return `${amountWorked.toLocaleString('tr-TR')} ₺`;
                const filesToBeWorked = row.accidentFile?.filesToBeWorked || [];
                const total = filesToBeWorked.reduce((sum, file) => sum + (Number(file?.fileExpense) || 0), 0);
                return total > 0 ? `${total.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1.2,
            minWidth: '130px'
        },
        {
            name: 'İlam Durumu',
            selector: row => {
                const v = row.accidentFile?.isSentenced;
                if (v === true) return 'İlamlı';
                if (v === false) return 'İlamsız';
                return '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Durum',
            selector: row => row.arbitrationState?.title || '-',
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Tahsilat',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'info',
                        size: 'sm',
                        outline: true,
                        onClick: () => handleOpenPaymentCollectionListModal(row),
                        title: 'Tahsilatları Görüntüle / Ekle'
                    }}
                >
                    <DollarSign size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true
        },
        {
            name: 'İş Ata/Kaldır',
            cell: (row) => {
                if (userRole !== 'COORDINATOR' && userRole !== 'MANAGEMENT' && userRole !== 'HEAD ENFORCEMENT') {
                    return null;
                }

                const appointedUserArray = row.appointedUser || [];
                const hasAssignment = appointedUserArray.length > 0;

                if (!hasAssignment) {
                    return (
                        <div
                            className="btn btn-outline-success d-flex align-items-center justify-content-center"
                            onClick={() => toggleAssignModal(row.id)}
                            disabled={isAssigning}
                            title="İş Ata"
                        >
                            <UserPlus size={18} />
                        </div>
                    );
                }

                return (
                    <div
                        className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                        onClick={() => handleRemoveAssignment(row.id)}
                        disabled={isRemovingAssignment}
                        title="İş Atamasını Kaldır"
                    >
                        <UserX size={18} />
                    </div>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '150px'
        },
        {
            name: 'İşlemler',
            cell: (row) => {
                if (!row.canEdit) {
                    return null;
                }

                return (
                    <Btn
                        attrBtn={{
                            color: 'primary',
                            size: 'sm',
                            outline: true,
                            onClick: () => navigate(`${process.env.PUBLIC_URL}/arbitration/${row.id}`),
                            title: 'Tahkim Detaylarını Görüntüle'
                        }}
                    >
                        <Edit size={18} />
                    </Btn>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '100px',
            center: true
        }
    ]), [handleRemoveAssignment, isAssigning, isRemovingAssignment, navigate, toggleAssignModal, userRole]);

    const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;
    const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length > 0;
    const hasAccidentDateSearch = accidentDateSearch && accidentDateSearch.trim().length > 0;
    const hasOtherSearches = hasTcIdSearch || hasAccidentDateSearch;

    return (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row>
                        <Col md="6" lg="4" xl="3">
                            <FormGroup>
                                <Label>Esas Numarası ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="Esas numarası ile ara... (en az 3 karakter)"
                                    value={caseNumberSearch}
                                    onChange={handleCaseNumberSearchChange}
                                    style={{ textTransform: 'uppercase' }}
                                    disabled={hasOtherSearches}
                                />
                                {caseNumberSearch && caseNumberSearch.trim().length > 0 && caseNumberSearch.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                                {hasOtherSearches && (
                                    <small className="text-muted">Diğer aramalar aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="6" lg="4" xl="3">
                            <FormGroup>
                                <Label>TC Kimlik Numarası / Vergi Numarası ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="TC kimlik numarası / Vergi numarası..."
                                    value={tcIdSearch}
                                    onChange={handleTcIdSearchChange}
                                    disabled={hasCaseNumberSearch}
                                    maxLength="11"
                                />
                                {tcIdSearch && tcIdSearch.trim().length > 0 && tcIdSearch.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                                {hasCaseNumberSearch && (
                                    <small className="text-muted">Esas numarası araması aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="6" lg="4" xl="3">
                            <FormGroup>
                                <Label>Plaka Kaza Tarihi</Label>
                                <Input
                                    type="date"
                                    value={accidentDateSearch}
                                    onChange={handleAccidentDateSearchChange}
                                    disabled={hasCaseNumberSearch}
                                />
                                {hasCaseNumberSearch && (
                                    <small className="text-muted">Esas numarası araması aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="6" lg="4" xl="3">
                            <FormGroup>
                                <Label>İcra Durumu Filtrele</Label>
                                <Input
                                    type="select"
                                    value={selectedState}
                                    onChange={handleArbitrationStateFilterChange}
                                    disabled={hasCaseNumberSearch || hasOtherSearches}
                                >
                                    <option value="">Tümü</option>
                                    {arbitrationStates.map(state => (
                                        <option key={state.id} value={state.id}>
                                            {state.title}
                                        </option>
                                    ))}
                                </Input>
                                {(hasCaseNumberSearch || hasOtherSearches) && (
                                    <small className="text-muted">Diğer aramalar aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="6" lg="4" xl="3">
                            <FormGroup>
                                <Label>İlam Durumu Filtrele</Label>
                                <Input
                                    type="select"
                                    value={selectedSentenced}
                                    onChange={handleSentencedFilterChange}
                                    disabled={hasCaseNumberSearch || hasOtherSearches}
                                >
                                    <option value="">Tümü</option>
                                    <option value="true">İlamlı</option>
                                    <option value="false">İlamsız</option>
                                </Input>
                                {(hasCaseNumberSearch || hasOtherSearches) && (
                                    <small className="text-muted">Diğer aramalar aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <DataTable
                        columns={columns}
                        data={items}
                        progressPending={loading}
                        pagination
                        paginationServer
                        stickyActionPosition="right"
                        paginationTotalRows={totalRows}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        paginationPerPage={pageSize}
                        paginationDefaultPage={page}
                        selectableRows={false}
                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                        noDataComponent={loading ? null : "İcrada bulunan dosya bulunamadı"}
                    />
                </Col>
            </Row>

            <Modal isOpen={assignModalOpen} toggle={toggleAssignModal}>
                <ModalHeader toggle={toggleAssignModal}>Personel Ata</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="enforcementUserSelect">Personel Seçin</Label>
                        <Input
                            type="select"
                            id="enforcementUserSelect"
                            value={selectedUserId}
                            onChange={(event) => setSelectedUserId(event.target.value)}
                        >
                            <option value="">Personel seçin...</option>
                            {employeeList.map(employee => (
                                <option key={employee.id} value={employee.id}>
                                    {employee.name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{ color: 'secondary', onClick: toggleAssignModal }}>
                        İptal
                    </Btn>
                    <Btn
                        attrBtn={{
                            color: 'primary',
                            onClick: handleAssignUser,
                            disabled: isAssigning || !selectedUserId
                        }}
                    >
                        {isAssigning ? 'Atanıyor...' : 'Ata'}
                    </Btn>
                </ModalFooter>
            </Modal>

            {/* Tahsilat Listesi Modalı */}
            <PaymentCollectionListModal
                isOpen={isPaymentCollectionListModalOpen}
                toggle={closePaymentCollectionListModal}
                title={`Tahsilatlar${currentAccidentFileForPayments?.fileNumber ? ` - Dosya ${currentAccidentFileForPayments.fileNumber}` : ''}`}
                paymentCollections={paymentCollections}
                isLoading={isPaymentCollectionsLoading}
                submittingId={finalCompensationSubmittingId}
                onEdit={handleEditPaymentCollection}
                onSendToCover={handleSendPaymentCollectionToCover}
                onAdd={handleAddPaymentCollection}
                isSendToCoverDisabled={(collection, isSubmitting) => isPaymentCollectionsLoading || !currentAccidentFileForPayments?.id || isSubmitting}
            />

            {/* Tahsilat Ekle/Düzenle Modalı */}
            <PaymentCollectionDataModal
                apiEndpoint={paymentCollectionApiUrl}
                isOpen={paymentCollectionModalOpen}
                toggle={togglePaymentCollectionModal}
                mode={paymentCollectionModalMode}
                selectedPaymentCollection={selectedPaymentCollection}
                refreshData={refreshPaymentCollections}
                accidentFile={currentAccidentFileForPayments?.id ? [String(currentAccidentFileForPayments.id)] : []}
                callForm={currentAccidentFileForPayments?.callForm
                    ? (Array.isArray(currentAccidentFileForPayments.callForm)
                        ? currentAccidentFileForPayments.callForm.map(String)
                        : [String(currentAccidentFileForPayments.callForm)])
                    : []}
                enforcement={currentAccidentFileForPayments?._enforcementId ? [String(currentAccidentFileForPayments._enforcementId)] : []}
            />
        </Container>
    );
};

export default TodoTabEnforcement;
